import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  map,
  catchError,
  tap,
  finalize,
  shareReplay,
  mergeMap,
} from 'rxjs/operators';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import {
  ScoreUtilsService,
  ScoreTimeSeriesContentParams,
} from 'src/app/shared/utils/score-utils.service';
import {
  BullBearScoreSeries,
  IndustryBenchScoreSeries,
  ScoreRequestTypes,
  ScoreTimeSeriesRequestBody,
} from 'src/app/shared/models/score-time-series.interface';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { TopEventsParamsService } from 'src/app/shared/utils/top-events-params.service';
import { QueryParamsService } from 'src/app/shared/utils/query-params.service';
import { HIERARCHY_ENTITY_TYPES } from 'src/app/shared/utils/common-data-params.service';

export interface ScoreDataPoint {
  date: string;
  score: number | null;
}

export interface SentimentDataPoint {
  date: string;
  quotient: number | null;
}

export interface SentimentState {
  quotientData: SentimentDataPoint[];
  period: number;
}

/** Basic entity info returned from score_ts endpoint */
export interface EntitySentimentInfo {
  type: string;
  alpha_id: string | null;
  compound_key: string | null;
  [key: string]: unknown; // Allow additional fields from getEntitiesInfo enrichment
}

/** Sentiment values matching isentiment response format */
export interface EntitySentimentValues {
  sentiment: number | null;
  sentiment_delta: number | null;
}

export interface EntitySentimentResponse {
  entity: EntitySentimentInfo;
  sentiment: EntitySentimentValues;
}

export interface FetchEntitySentimentOptions {
  period: number;
  newsday?: string;
  collapseOn?: boolean;
  getSanctions?: boolean;
}

export const SCORE_SUPPORTED_ENTITY_TYPES = [
  'company',
  'pne',
  'industry',
  'supersector',
  'sector',
  'subsector',
] as const;

const EMPTY_SENTIMENT_STATE: SentimentState = {
  quotientData: [],
  period: 0,
};

export interface NewsScoreState {
  scoreData: ScoreDataPoint[];
  lastScore: number | null;
  scoreType: string;
  trendType: 'bull' | 'bear' | null;
  trendValue: string | null;
}

const EMPTY_STATE: NewsScoreState = {
  scoreData: [],
  lastScore: null,
  scoreType: '',
  trendType: null,
  trendValue: null,
};

/**
 * Shared service for news page score data.
 * Uses BehaviorSubject pattern to provide reactive state management.
 * Pattern from scanner-score-state.service.ts
 */
@Injectable({
  providedIn: 'root',
})
export class NewsScoreStateService {
  private readonly scoreStateSubject =
    new BehaviorSubject<NewsScoreState | null>(null);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  readonly scoreState$ = this.scoreStateSubject.asObservable();

  readonly loading$ = this.loadingSubject.asObservable();

  readonly error$ = this.errorSubject.asObservable();

  private readonly sentimentStateSubject =
    new BehaviorSubject<SentimentState | null>(null);
  private readonly sentimentLoadingSubject = new BehaviorSubject<boolean>(
    false,
  );

  readonly sentimentState$ = this.sentimentStateSubject.asObservable();

  readonly sentimentLoading$ = this.sentimentLoadingSubject.asObservable();

  private pendingRequest$: Observable<NewsScoreState> | null = null;
  private pendingScoreType: string | null = null;

  private pendingSentimentRequest$: Observable<SentimentState> | null = null;
  private pendingSentimentPeriod: number | null = null;

  constructor(
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private entityInfoService: EntityInfoService,
    private topEventsParams: TopEventsParamsService,
    private queryParamsService: QueryParamsService,
  ) {}

  /**
   * Entity industry benchmark (quant `score_ts_industry_bench`, or the
   * `bull_bear` variant for 'bb'). Legacy contract: period forced to 30 (URL
   * `period` never forwarded), `credit_risk` resolved to `credit_watch`.
   * Does NOT update shared state — one-off fetch.
   */
  fetchIndustryBenchmark(
    scoreId: string,
    qparam: { type: string; id: string; time?: string },
  ): Observable<IndustryBenchScoreSeries | BullBearScoreSeries> {
    const time = qparam.time || '7';
    if (TopEventsParamsService.isBullBearScoreKey(scoreId)) {
      return this.scoreTimeSeriesService.getEntityBullBearIndustryBenchScore(
        qparam.type,
        qparam.id,
        this.topEventsParams.buildBullBearIndustryBenchRequest({ time }),
      );
    }
    return this.scoreTimeSeriesService.getEntityIndustryBenchScore(
      qparam.type,
      qparam.id,
      this.topEventsParams.buildIndustryBenchRequest({
        scoreKey: ScoreUtilsService.resolveScoreKey(scoreId),
        time,
      }),
    );
  }

  /**
   * Fetches score data and updates the reactive state.
   * Returns cached pending request if same score type is already being fetched.
   */
  fetchScoreData(scoreType: ScoreRequestTypes): Observable<NewsScoreState> {
    const scoreKey = JSON.stringify(scoreType);

    if (this.pendingRequest$ && this.pendingScoreType === scoreKey) {
      return this.pendingRequest$;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);
    this.pendingScoreType = scoreKey;

    this.pendingRequest$ = this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {
          collapse_on: ['participant'],
        },
        body: {
          score_type: scoreType,
        },
      })
      .pipe(
        map((response) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          const lastScore = ScoreUtilsService.getLastScore(timeSeries);
          const { trendType, trendValue } =
            ScoreUtilsService.getBullBearTrend(lastScore);

          return {
            scoreData: timeSeries,
            lastScore,
            scoreType: scoreKey,
            trendType,
            trendValue,
          };
        }),
        tap((state) => {
          this.scoreStateSubject.next(state);
        }),
        catchError((error) => {
          const errorMessage = error?.message || 'Failed to load score data';
          this.errorSubject.next(errorMessage);
          const emptyState = { ...EMPTY_STATE, scoreType: scoreKey };
          this.scoreStateSubject.next(emptyState);
          return of(emptyState);
        }),
        finalize(() => {
          this.loadingSubject.next(false);
          this.pendingRequest$ = null;
          this.pendingScoreType = null;
        }),
        shareReplay(1),
      );

    return this.pendingRequest$;
  }

  /**
   * Fetches score data with a custom time range.
   * Does NOT update shared state - use for one-off requests like caution flag checks.
   */
  fetchScoreDataWithTime(
    scoreType: ScoreRequestTypes,
    time: string,
  ): Observable<NewsScoreState> {
    const scoreKey = JSON.stringify(scoreType);

    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {
          collapse_on: ['participant'],
        },
        body: {
          score_type: scoreType,
          time,
        },
      })
      .pipe(
        map((response) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          const lastScore = ScoreUtilsService.getLastScore(timeSeries);
          const { trendType, trendValue } =
            ScoreUtilsService.getBullBearTrend(lastScore);

          return {
            scoreData: timeSeries,
            lastScore,
            scoreType: scoreKey,
            trendType,
            trendValue,
          };
        }),
        catchError(() => of({ ...EMPTY_STATE, scoreType: scoreKey })),
      );
  }

  /**
   * Fetches sentiment data and updates the reactive state.
   * Returns cached pending request if same period is already being fetched.
   */
  fetchSentimentData(period: number): Observable<SentimentState> {
    if (
      this.pendingSentimentRequest$ &&
      this.pendingSentimentPeriod === period
    ) {
      return this.pendingSentimentRequest$;
    }

    this.sentimentLoadingSubject.next(true);
    this.pendingSentimentPeriod = period;

    this.pendingSentimentRequest$ = this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {
          collapse_on: ['participant'],
        },
        body: {
          score_type: { period },
        },
      })
      .pipe(
        map((response) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          return {
            quotientData: timeSeries.map((item) => ({
              date: item.date,
              quotient: item.score,
            })),
            period,
          };
        }),
        tap((state) => {
          this.sentimentStateSubject.next(state);
        }),
        catchError(() => {
          const emptyState = { ...EMPTY_SENTIMENT_STATE, period };
          this.sentimentStateSubject.next(emptyState);
          return of(emptyState);
        }),
        finalize(() => {
          this.sentimentLoadingSubject.next(false);
          this.pendingSentimentRequest$ = null;
          this.pendingSentimentPeriod = null;
        }),
        shareReplay(1),
      );

    return this.pendingSentimentRequest$;
  }

  /**
   * Fetches sentiment data with a custom time range.
   * Does NOT update shared state - use for one-off requests like caution flag checks.
   * Returns quotient_ts format compatible with legacy expectations.
   */
  fetchSentimentDataWithTime(
    period: number,
    time: string,
  ): Observable<SentimentState> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {
          collapse_on: ['participant'],
        },
        body: {
          score_type: { period },
          time,
        },
      })
      .pipe(
        map((response) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          return {
            quotientData: timeSeries.map((item) => ({
              date: item.date,
              quotient: item.score,
            })),
            period,
          };
        }),
        catchError(() => of({ ...EMPTY_SENTIMENT_STATE, period })),
      );
  }

  fetchEntitySentiment(
    options: FetchEntitySentimentOptions,
  ): Observable<EntitySentimentResponse> {
    const { period, collapseOn, getSanctions, newsday } = options;
    const sentimentPeriod = period || 7;
    const body: ScoreTimeSeriesContentParams = {
      score_type: { period: sentimentPeriod },
      newsday,
    };

    const customParams: Partial<ScoreTimeSeriesRequestBody> = {};
    if (collapseOn) {
      customParams.collapse_on = ['participant'];
    }

    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams,
        body,
      })
      .pipe(
        mergeMap((response) => {
          const result = response.score_ts?.[0];
          const timeSeries = result?.score_ts || [];
          const entityInfo = result?.entity_info;

          const sentiment = ScoreUtilsService.getLastScore(timeSeries);

          let sentimentDelta: number | null = null;
          if (sentiment !== null && timeSeries.length >= 2) {
            for (let i = timeSeries.length - 1; i >= 1; i--) {
              if (timeSeries[i].score != null) {
                const prevScore = timeSeries[i - 1]?.score;
                if (prevScore != null) {
                  sentimentDelta = timeSeries[i].score - prevScore;
                }
                break;
              }
            }
          }

          const sentimentValues: EntitySentimentValues = {
            sentiment,
            sentiment_delta: sentimentDelta,
          };

          const displayKey = this.resolveDisplayEntityKey(entityInfo);
          if (displayKey) {
            const [displayType, displayId] = displayKey.split(':');
            const fallbackEntity = {
              type: displayType || '',
              alpha_id: displayId || null,
              compound_key: displayKey,
            };
            return this.entityInfoService
              .getEntitiesInfo([displayKey], 'object', getSanctions ?? false)
              .pipe(
                map((enrichedEntities) => ({
                  entity: enrichedEntities[displayKey] || fallbackEntity,
                  sentiment: sentimentValues,
                })),
                catchError(() =>
                  of({ entity: fallbackEntity, sentiment: sentimentValues }),
                ),
              );
          }

          return of({
            entity: {
              type: entityInfo?.type || '',
              alpha_id: entityInfo?.alpha_id || null,
              compound_key: entityInfo?.compound_key || null,
            },
            sentiment: sentimentValues,
          });
        }),
        catchError(() =>
          of({
            entity: { type: '', alpha_id: null, compound_key: null },
            sentiment: { sentiment: null, sentiment_delta: null },
          }),
        ),
      );
  }

  /**
   * The entity the title should describe. Hierarchy drill-downs query the
   * constituent portfolio, so the response's collapsed `portfolio:portfolio`
   * aggregate isn't displayable — resolve back to the URL entity (e.g.
   * `sector:media`) for enrichment. Everything else keeps the response entity.
   */
  private resolveDisplayEntityKey(
    entityInfo: { type?: string; compound_key?: string | null } | undefined,
  ): string | null {
    if (!entityInfo?.compound_key) {
      return null;
    }
    if (entityInfo.type === 'portfolio') {
      const { type, id } = this.queryParamsService.queryParams;
      if (
        type &&
        id &&
        HIERARCHY_ENTITY_TYPES.includes(
          type as (typeof HIERARCHY_ENTITY_TYPES)[number],
        )
      ) {
        return `${type}:${id}`;
      }
    }
    return entityInfo.compound_key;
  }

  /**
   * Resolves score type using ScoreUtilsService
   */
  resolveScoreType(scoreId: string): ScoreRequestTypes {
    return this.scoreUtils.resolveScoreType(scoreId);
  }

  supportsScoreData(
    entityType: string | undefined,
    isFolio: boolean,
    hasIsin: boolean,
  ): boolean {
    if (isFolio || hasIsin) {
      return true;
    }
    if (!entityType) {
      return false;
    }
    return SCORE_SUPPORTED_ENTITY_TYPES.includes(
      entityType as (typeof SCORE_SUPPORTED_ENTITY_TYPES)[number],
    );
  }

  /**
   * Clears the current state
   */
  clearState(): void {
    this.scoreStateSubject.next(null);
    this.loadingSubject.next(false);
    this.errorSubject.next(null);
    this.pendingRequest$ = null;
    this.pendingScoreType = null;
    this.sentimentStateSubject.next(null);
    this.sentimentLoadingSubject.next(false);
    this.pendingSentimentRequest$ = null;
    this.pendingSentimentPeriod = null;
  }
}
